import React, { useRef, useState } from 'react';
import { ScanLine, Camera, Trash2, Loader2, Flag, CheckCircle2 } from 'lucide-react';
import api, { API } from '../lib/api';
import { toast } from '../hooks/use-toast';

const err = (e) => toast({ title: 'Erro', description: e.response?.data?.detail || 'Falha na operação' });

const PhotoGrid = ({ photos, canEdit, onDelete }) => (
  <div className="flex flex-wrap gap-2">
    {photos.map((p) => (
      <div key={p.id} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-border bg-black/40">
        <a href={`${API}/deals/files/${p.path}`} target="_blank" rel="noreferrer">
          <img src={`${API}/deals/files/${p.path}`} alt={p.kind} className="w-full h-full object-cover" data-testid={`svc-photo-${p.id}`} />
        </a>
        {canEdit && (
          <button data-testid={`svc-photo-del-${p.id}`} onClick={() => onDelete(p.id)} className="absolute top-1 right-1 p-1 rounded bg-black/70 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3" /></button>
        )}
      </div>
    ))}
    {photos.length === 0 && <p className="text-[11px] text-muted-foreground/60">Nenhuma foto ainda.</p>}
  </div>
);

export const ServiceInspectionPanel = ({ d, onChanged, canEdit }) => {
  const [uploading, setUploading] = useState('');
  const [marking, setMarking] = useState(false);
  const scannerRef = useRef(null);
  const vehicleRef = useRef(null);
  const svc = d.service;

  if (!svc) return <p className="text-sm text-muted-foreground/70 py-4 text-center">O veículo ainda não chegou ao Service (aguardando Registration Completed).</p>;

  const done = svc.checklist.filter((c) => c.checked).length;
  const scannerPhotos = svc.photos.filter((p) => p.kind === 'scanner');
  const vehiclePhotos = svc.photos.filter((p) => p.kind === 'vehicle');
  const isDone = d.service_status === 'ready' || d.delivery_status === 'delivered';
  const editable = canEdit && !isDone;

  const toggle = async (key, checked) => {
    try { await api.put(`/deals/${d.id}/service/checklist`, { key, checked }); onChanged(); } catch (e) { err(e); }
  };

  const upload = async (kind, files) => {
    if (!files?.length) return;
    setUploading(kind);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('kind', kind);
        await api.post(`/deals/${d.id}/service/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast({ title: 'Foto(s) enviada(s)' });
      onChanged();
    } catch (e) { err(e); } finally { setUploading(''); }
  };

  const delPhoto = async (pid) => {
    try { await api.delete(`/deals/${d.id}/service/photos/${pid}`); onChanged(); } catch (e) { err(e); }
  };

  const markReady = async () => {
    setMarking(true);
    try {
      await api.post(`/deals/${d.id}/service/ready`);
      toast({ title: '🚗 READY FOR DELIVERY', description: 'Vendedor e gerência notificados.' });
      onChanged();
    } catch (e) { err(e); } finally { setMarking(false); }
  };

  return (
    <div className="space-y-4" data-testid="service-inspection-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Delivery Inspection Checklist</p>
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${done === svc.checklist.length ? 'text-emerald-400 bg-emerald-400/10' : 'text-amber-400 bg-amber-400/10'}`} data-testid="svc-progress">{done}/{svc.checklist.length}</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${(done / svc.checklist.length) * 100}%` }} /></div>
      <div className="space-y-1.5">
        {svc.checklist.map((c) => (
          <label key={c.key} data-testid={`svc-check-${c.key}`} className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${c.checked ? 'border-emerald-400/20 bg-emerald-400/[0.04]' : 'border-border/70 bg-muted/40'} ${editable ? 'cursor-pointer hover:border-foreground/25' : 'opacity-80'}`}>
            <input type="checkbox" disabled={!editable} checked={c.checked} onChange={(e) => toggle(c.key, e.target.checked)} className="mt-0.5 accent-primary w-4 h-4" />
            <div className="flex-1 min-w-0">
              <p className={`text-[13px] font-medium ${c.checked ? 'text-emerald-300' : ''}`}>{c.label}</p>
              {c.checked && <p className="text-[10px] text-muted-foreground/60">✓ {c.checked_by}</p>}
              {c.requires_photo === 'scanner' && <p className="text-[10px] text-amber-400/80 mt-0.5">Requer foto do scanner abaixo</p>}
            </div>
          </label>
        ))}
      </div>

      <div className="rounded-lg border border-border/70 p-3.5 space-y-2.5">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><ScanLine className="w-3.5 h-3.5" /> Foto do Scanner (obrigatória)</p>
          {editable && <button data-testid="svc-upload-scanner" onClick={() => scannerRef.current?.click()} disabled={uploading === 'scanner'} className="text-[11px] px-3 py-1.5 rounded-md font-semibold bg-muted/50 border border-border hover:bg-muted/80 flex items-center gap-1.5">{uploading === 'scanner' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />} Enviar</button>}
        </div>
        <input ref={scannerRef} type="file" accept="image/*" className="hidden" onChange={(e) => { upload('scanner', Array.from(e.target.files)); e.target.value = ''; }} />
        <PhotoGrid photos={scannerPhotos} canEdit={editable} onDelete={delPhoto} />
      </div>

      <div className="rounded-lg border border-border/70 p-3.5 space-y-2.5">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Fotos do Veículo (mín. 1)</p>
          {editable && <button data-testid="svc-upload-vehicle" onClick={() => vehicleRef.current?.click()} disabled={uploading === 'vehicle'} className="text-[11px] px-3 py-1.5 rounded-md font-semibold bg-muted/50 border border-border hover:bg-muted/80 flex items-center gap-1.5">{uploading === 'vehicle' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />} Enviar</button>}
        </div>
        <input ref={vehicleRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { upload('vehicle', Array.from(e.target.files)); e.target.value = ''; }} />
        <PhotoGrid photos={vehiclePhotos} canEdit={editable} onDelete={delPhoto} />
      </div>

      {isDone ? (
        <p className="text-[12px] text-emerald-400 font-semibold flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Inspeção concluída por {svc.ready_by} — READY FOR DELIVERY</p>
      ) : (
        canEdit && (
          <button data-testid="svc-mark-ready" onClick={markReady} disabled={marking} className="w-full py-3 rounded-lg text-sm font-black uppercase tracking-wide bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
            {marking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Marcar Ready for Delivery
          </button>
        )
      )}
    </div>
  );
};

export const DeliveryPanel = ({ d, onChanged, canDeliver }) => {
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), time: '', present: false, notes: '' });
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    setSaving(true);
    try {
      await api.post(`/deals/${d.id}/delivery`, { date: form.date, time: form.time, customer_present: form.present, notes: form.notes });
      toast({ title: '🏁 VEHICLE DELIVERED', description: 'Ciclo do deal encerrado. Parabéns!' });
      onChanged();
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  if (d.delivery_status === 'delivered') {
    const dv = d.delivery || {};
    return (
      <div className="rounded-lg border border-emerald-400/20 bg-emerald-400/[0.05] p-4 space-y-1" data-testid="delivery-done-panel">
        <p className="text-sm font-black uppercase text-emerald-400 flex items-center gap-2"><Flag className="w-4 h-4" /> Delivered</p>
        <p className="text-[13px] text-foreground/75">{dv.date} às {dv.time} · Cliente presente ✓</p>
        <p className="text-[11px] text-muted-foreground">Registrado por {dv.delivered_by}</p>
        {dv.notes && <p className="text-[12px] text-muted-foreground mt-1">{dv.notes}</p>}
      </div>
    );
  }
  if (d.status !== 'READY_FOR_DELIVERY') return null;
  if (!canDeliver) return <p className="text-[12px] text-muted-foreground">Veículo pronto para entrega. Apenas o vendedor do deal ou a gerência pode registrar a entrega.</p>;

  const inputCls = 'w-full bg-muted/40 border border-border rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors';
  return (
    <div className="rounded-lg border border-primary/30 bg-primary/[0.05] p-4 space-y-3" data-testid="delivery-form">
      <p className="text-xs font-black uppercase tracking-wide text-primary flex items-center gap-1.5"><Flag className="w-3.5 h-3.5" /> Registrar Entrega Final</p>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-muted-foreground mb-1.5 block">Data</label><input data-testid="delivery-date" type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground mb-1.5 block">Hora</label><input data-testid="delivery-time" type="time" className={inputCls} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
      </div>
      <input data-testid="delivery-notes" className={inputCls} placeholder="Observações (opcional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      <label className="flex items-center gap-2.5 cursor-pointer" data-testid="delivery-present-label">
        <input data-testid="delivery-present" type="checkbox" checked={form.present} onChange={(e) => setForm({ ...form, present: e.target.checked })} className="accent-primary w-4 h-4" />
        <span className="text-[13px] font-medium">Confirmo que o cliente esteve presente na entrega</span>
      </label>
      <button data-testid="delivery-confirm" onClick={confirm} disabled={saving || !form.date || !form.time || !form.present} className="w-full py-3 rounded-lg text-sm font-black uppercase tracking-wide bg-primary hover:bg-primary/90 disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />} Confirmar Entrega
      </button>
    </div>
  );
};
